// Pure joining/ordering logic for the questionnaire group level (genesis#183).
//
// genesis serves two flat lists: `questionnaireGroups` and `questionnaires`
// (each carrying `questionnaireGroupId` + `position`). The admin joins them
// here rather than reading `QuestionnaireGroup.parts`, because `parts` only
// contains ACTIVE members while the admin has to see and reorder inactive
// ones too.
//
// Structural parameter types, not the generated operation types: these
// functions are the tested core and should not have to change when a
// selection set grows.

import { groupImageSrc, localizedText } from "@/lib/questionnaire-group";
import type { LocalizedTextLike } from "@/lib/questionnaire-group";
import type {
  QuestionnaireGroupsAdminQuery,
  QuestionnairesListQuery,
} from "@/graphql/generated/operations";

/** The two rows the admin joins. Aliased here so both editors name them the same. */
export type GroupRow = QuestionnaireGroupsAdminQuery["questionnaireGroups"][number];
export type QuestionnaireRow = QuestionnairesListQuery["questionnaires"][number];

export type GroupStatus = "DRAFT" | "PUBLISHED";

export type { LocalizedTextLike };

export interface GroupLike {
  questionnaireGroupId: string;
  name: LocalizedTextLike;
  position: number;
  status: string;
}

export interface MemberLike {
  questionnaireId: string;
  name: string;
  status: string;
  questionnaireGroupId?: string | null;
  position?: number | null;
}

export interface GroupSection<G extends GroupLike, M extends MemberLike> {
  group: G;
  members: M[];
}

export interface GroupedList<G extends GroupLike, M extends MemberLike> {
  sections: GroupSection<G, M>[];
  ungrouped: M[];
}

/**
 * What the display name falls back to when a locale variant is missing. Grow
 * reads the same groups and has to fall back identically, so the rule lives in
 * lib/ and this is the admin's name for it.
 */
export function groupName(name: LocalizedTextLike): string {
  return localizedText(name);
}

/**
 * Catalogue order, mirroring genesis: position first, English name as the
 * tie-breaker. (group, position) is deliberately not unique server-side, so
 * ties are real and have to resolve the same way here as they do in Grow.
 */
export function sortGroups<G extends GroupLike>(groups: readonly G[]): G[] {
  return [...groups].sort(
    (a, b) =>
      a.position - b.position ||
      (a.name.english || groupName(a.name)).localeCompare(b.name.english || groupName(b.name)),
  );
}

/** Order within a group, mirroring genesis: position, then name. */
export function sortMembers<M extends MemberLike>(members: readonly M[]): M[] {
  return [...members].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0) || a.name.localeCompare(b.name),
  );
}

/**
 * Joins the two flat lists into sections plus the bucket of unassigned
 * questionnaires.
 *
 * `catalogueLoaded` says whether `groups` is the real catalogue or just the
 * empty placeholder of a query still in flight. It only decides what happens to
 * a membership pointing at a group that is not in `groups`: once the catalogue
 * is known that means the row is orphaned and belongs in the loose bucket,
 * where it stays reachable; before that it means nothing at all, and filing
 * every grouped questionnaire under "no group" for one frame would flash a
 * catalogue warning at rows that are perfectly fine.
 */
export function buildGroupedList<G extends GroupLike, M extends MemberLike>(
  groups: readonly G[],
  questionnaires: readonly M[],
  catalogueLoaded = true,
): GroupedList<G, M> {
  const known = new Set(groups.map((g) => g.questionnaireGroupId));
  const byGroup = new Map<string, M[]>();
  const ungrouped: M[] = [];
  for (const q of questionnaires) {
    const groupId = q.questionnaireGroupId;
    if (!groupId) {
      ungrouped.push(q);
      continue;
    }
    if (!known.has(groupId)) {
      if (catalogueLoaded) ungrouped.push(q);
      continue;
    }
    const bucket = byGroup.get(groupId);
    if (bucket) bucket.push(q);
    else byGroup.set(groupId, [q]);
  }

  return {
    sections: sortGroups(groups).map((group) => ({
      group,
      members: sortMembers(byGroup.get(group.questionnaireGroupId) ?? []),
    })),
    ungrouped: sortMembers(ungrouped),
  };
}

export function hasActiveMember(members: readonly MemberLike[]): boolean {
  return members.some((m) => m.status === "ACTIVE");
}

/**
 * The three states the list has to speak about in one voice. `draft` and
 * `emptyGroup` both mean "no tile in the play catalogue" and are rendered with
 * the same sentence; they are kept apart so the group form can warn specifically
 * before publishing an empty group.
 */
export type GroupVisibility = "visible" | "draft" | "emptyGroup";

export function groupVisibility(
  status: string,
  members: readonly MemberLike[],
  itemCount?: number,
): GroupVisibility {
  if (status !== "PUBLISHED") return "draft";
  // genesis documents itemCount as the tile's own gate ("0 means Grow renders no
  // tile"), and it counts ACTIVE questions of ACTIVE members, so it also catches
  // an active member that has nothing left to answer. It is only absent before
  // the first save, where the member list is all there is to go on.
  if (itemCount !== undefined && itemCount === 0) return "emptyGroup";
  return hasActiveMember(members) ? "visible" : "emptyGroup";
}

export interface PositionChange {
  questionnaireId: string;
  position: number;
}

/**
 * Moving one member one slot up or down, expressed as the minimal set of
 * assignQuestionnaireToGroup calls.
 *
 * genesis never compacts positions and does not make them unique, so the
 * stored numbers may have holes (a removed member) or ties. Renumbering the
 * whole list to 0..n-1 and emitting only the rows that actually change keeps
 * an adjacent swap at two mutations in the normal case while healing holes and
 * ties on the way. An out-of-range move is a no-op rather than an error: the
 * arrows at the ends of the list are disabled, and a plan of zero changes is a
 * legitimate answer.
 */
export function reorderPlan(
  members: readonly MemberLike[],
  index: number,
  direction: -1 | 1,
): PositionChange[] {
  const target = index + direction;
  const moved = members[index];
  const displaced = members[target];
  if (!moved || !displaced) {
    return [];
  }
  const next = [...members];
  next[index] = displaced;
  next[target] = moved;

  return next
    .map((member, i) => ({ member, position: i }))
    .filter(({ member, position }) => (member.position ?? null) !== position)
    .map(({ member, position }) => ({ questionnaireId: member.questionnaireId, position }));
}

/** genesis `File` → an <img src>. Binary is base64 without the data: prefix. */
export const imageSrc = groupImageSrc;

/**
 * Soft target for the group description. genesis stores whatever it is given;
 * the catalogue tile clamps it, so this is guidance for the editor rather than
 * a limit that could reject a save.
 */
export const GROUP_DESCRIPTION_TARGET = 120;

/**
 * The slot a new group would land in. Mirrors genesis's `nextGroupPosition`
 * (max + 1 over every group including drafts, empty table gives 0) so the form
 * shows the position the server would have picked instead of leaving it blank.
 */
export function nextGroupPosition(groups: readonly GroupLike[]): number {
  return groups.reduce((max, g) => Math.max(max, g.position + 1), 0);
}

/** Highest position genesis accepts: the column is int2 and negatives are rejected. */
export const MAX_POSITION = 32767;

/**
 * Highest answer weight genesis can store. `qnr_answer_choice.weight` is int2, and
 * QuestionnaireController narrowed with a bare `Integer.shortValue()`, so 40000 was silently stored as
 * -25536 while the API echoed back 40000 — poisoning every score SelfScoreCalculator averages from that
 * question. Bounding at the int2 ceiling (rather than the 0..100 the seeds actually use) prevents the
 * wrap without inventing a product rule that existing rows might violate.
 */
export const MAX_WEIGHT = 32767;
